import React from 'react';
import { Table, ScrollArea, TextInput, Button, Checkbox, Paper, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconSearch } from '@tabler/icons-react';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable';
import { useElementSize } from '@mantine/hooks';
import { useGetEmailProblems } from '../../../global/entities/EmailProblem/queries/useGetEmailProblems';
import { useSendVerificationMutation } from '../../../global/entities/EmailProblem/mutations/useSendVerificationMutation';
import { EmailProblemType } from '../../../global/entities/EmailProblem/EmailProblem';
import { Loading } from '../../../global/component/organism/Loading/Loading';

const EmailProblemsTableContent: React.FC = () => {
  const { t } = useTranslation();
  const { search, setSearch, sortedData, setSorting, sortBy, reverseSortDirection } = useTable();
  const { ref } = useElementSize();
  const { data: emailProblems, isLoading } = useGetEmailProblems();
  const sendVerificationMutation = useSendVerificationMutation();

  if (isLoading) 
    return <Loading loadingMessage={t('global.loading')} />;

  if (!emailProblems || emailProblems.length === 0) {
    return (
      <Paper radius="lg" shadow="sm" p="md">
        <Text>{t('emailProblems.noProblems')}</Text>
      </Paper>
    );
  }

  const handleSendVerification = (id: number) => {
    sendVerificationMutation.mutate(id);
  };

  return (
    <Paper radius="lg" shadow="sm" p="md" ref={ref}>
      <Stack>
        <TextInput
          placeholder={t('global.search')}
          rightSection={<IconSearch />}
          value={search}
          color={'var(--mantine-color-default-color)'}
          width={'100%'}
          radius={'md'}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <ScrollArea>
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Th
                  sorted={sortBy === 'email'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('email', emailProblems)}
                >
                  {t('global.email')}
                </Th>
                <Th
                  sorted={sortBy === 'problemType'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('problemType', emailProblems)}
                >
                  {t('global.problem')}
                </Th>
                <Table.Th>{t('emailProblems.status')}</Table.Th>
                <Th
                  sorted={sortBy === 'verificationStarted'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('verificationStarted', emailProblems)}
                >
                  {t('global.verificationStarted')}
                </Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(sortedData.length > 0 ? sortedData : emailProblems).filter(row => row.problemType !== EmailProblemType.SoftBounce).map((row, idx) => (
                <Table.Tr key={row.email} style={{ background: idx % 2 === 1 ? undefined : '#E9ECEF' }}>
                  <Table.Td>{row.email}</Table.Td>
                  <Table.Td>{t(`emailProblems.${EmailProblemType[row.problemType].toLowerCase()}`)}</Table.Td>
                  <Table.Td>{row.status || '-'}</Table.Td>
                  <Table.Td>
                    <Checkbox checked={row.verificationStarted} readOnly />
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="default"
                      loading={sendVerificationMutation.isPending}
                      onClick={() => handleSendVerification(row.id)}
                    >
                      {t('emailProblems.sendVerification')}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
          *{t('emailProblems.complaintNote')}
        </div>
      </Stack>
    </Paper>
  );
};

const EmailProblemsTable: React.FC = () => {
  return (
    <TableContextProvider>
      <EmailProblemsTableContent />
    </TableContextProvider>
  );
};

export default EmailProblemsTable; 
